// @flow

import memoize from 'memoize-weak';
import { findLast, isEmpty, last } from 'lodash';

import Errors from 'app/errors';

import type { FlowTaskState } from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';
import type { Task } from 'app/history/types';

/**
 * Task state utility functions that are flow typed.
 *
 * TaskUttils is not flow typed, and will take a while to flow type that file.
 * So put any new task state utility functions in this file, and flow type them.
 * Once we flow type TaskStateUtils.js, we can move the functions in this file there.
 */

const getNextTaskStateItem = <T>(prevItem: T, runItem: T): T =>
  prevItem ? { ...prevItem, ...runItem } : runItem;

const getNextBreadcrumbs = (previousBreadcrumbs, runBreadcrumbs) => {
  if (!previousBreadcrumbs) {
    return runBreadcrumbs;
  }
  if (isEmpty(runBreadcrumbs)) {
    return previousBreadcrumbs;
  }
  return [...previousBreadcrumbs, last(runBreadcrumbs)];
};

/**
 * Compute the next task state from the previous task state
 * and the task state that was just ran.
 *
 * We merge the previousState and runState. Except for the breadcrumbs,
 * top level, input, output, and status values in the runState override
 * matching keys in the previousState. The last breadcrumb value in the
 * runState is appended to the breadcrumb values in the previousState.
 *
 * For more details see the unit test.
 */
export const getNextTaskState = (
  previousState: ?FlowTaskState,
  runState: FlowTaskState
) => {
  if (!previousState) {
    return runState;
  }

  return {
    ...previousState,
    ...runState,
    input: getNextTaskStateItem(previousState.input, runState.input),
    output: getNextTaskStateItem(previousState.output, runState.output),
    statuses: getNextTaskStateItem(previousState.statuses, runState.statuses),
    breadcrumbs: getNextBreadcrumbs(
      previousState.breadcrumbs,
      runState.breadcrumbs
    ),
  };
};

export const getErrorForNode = (memoize((taskState, nodeId) => {
  if (!taskState.breadcrumbs) {
    return undefined;
  }

  const lastNodeBreadcrumb = findLast(
    taskState.breadcrumbs,
    ({ object_id }) => object_id === nodeId
  );

  if (!lastNodeBreadcrumb) {
    return undefined;
  }

  // TODO: Remove support for bundle-less errors once we there are no more cache in the Node states?
  const bundle =
    lastNodeBreadcrumb.error_bundle ||
    (lastNodeBreadcrumb.error && {
      title: lastNodeBreadcrumb.error,
    });

  if (!bundle) {
    return undefined;
  }

  return Errors.SampleError({
    ...bundle,
    step: lastNodeBreadcrumb.step,
  });
}): (taskState: FlowTaskState | Task, nodeId: ID) => ?Errors.SampleError);
